import * as constants from '../appConstants';
import { signPayload } from './signatureHelper';
import { cloneDeep } from 'lodash';

export function buildButtonStrings(buttonType: string, buttonData:any, schema: any, authValues: any) {
    const config = buildButtonConfig(buttonType, buttonData, authValues, schema);
    const code = buildButtonCode(authValues, buttonType, config, schema);

    return ({
        buttonSchema: code.buttonSchema,
        copyButtonCode: code.copyButtonCode,
        divStyle: code.divStyle,
        dotNetSDK: buildSdkCodeForDotNet(authValues, config.payloadJSON),
        phpSDK: buildSdkCodeForPHP(authValues, config.payloadJSON),
        javaSDK: buildSdkCodeForJava(authValues, config.payloadJSON),
        nodeJsSDK: buildSdkCodeForNodeJS(authValues, config.payloadJSON)
    });
}

export function isAdditionalPaymentWorkflow(button: string) {
    return ((button === constants.ButtonTypes.APB_ONE_TIME) || (button === constants.ButtonTypes.APB_RECURRING));
}

export function isOneTimeWorkflow(button: string) {
    return ((button === constants.ButtonTypes.ONE_TIME) || (button === constants.ButtonTypes.APB_ONE_TIME))
}

export function isRecurringWorkflow(button: string) {
    return ((button === constants.ButtonTypes.RECURRING) || (button === constants.ButtonTypes.APB_RECURRING))
}

export function isPaymentMethodOnFileWorkflow(button: string) {
    return (button === constants.ButtonTypes.PAYMENT_METHOD_ON_FILE);
}

function buildButtonConfig(buttonType: string, originalButtonData: any, authValues: any, schema: any) {
    const buttonData = cloneDeep(originalButtonData);

    let configString;

    if (buttonType === constants.ButtonTypes.AMAZON_SIGN_IN) {
        const config = {...buttonData.signInConfig};
        // convert CSV scopes into an array
        if ((config[constants.signIn.SIGN_IN_SCOPES]) && (config[constants.signIn.SIGN_IN_SCOPES].trim().length > 0)) {
            config[constants.signIn.SIGN_IN_SCOPES] =
                config[constants.signIn.SIGN_IN_SCOPES].split(',').map(string => string.trim())
        } else {
            delete config[constants.signIn.SIGN_IN_SCOPES];
        }

        configString = JSON.stringify(config);
    } else {

        // convert CSV scopes into an array
        if ((buttonData.createCheckoutSessionConfig[constants.SCOPES]) && (buttonData.createCheckoutSessionConfig[constants.SCOPES].trim().length > 0)) {
            buttonData.createCheckoutSessionConfig[constants.SCOPES] =
                buttonData.createCheckoutSessionConfig[constants.SCOPES].split(',').map(string => string.trim())
        } else {
            delete buttonData.createCheckoutSessionConfig[constants.SCOPES];
        }

	if (isOneTimeWorkflow(buttonType)) {
            buttonData.createCheckoutSessionConfig[constants.CHARGE_PERMISSION_TYPE] = constants.ChargePermissionTypes.ONE_TIME;
        } else if (isRecurringWorkflow(buttonType)) {
            buttonData.createCheckoutSessionConfig[constants.CHARGE_PERMISSION_TYPE] = constants.ChargePermissionTypes.RECURRING;
            if (buttonData.amount.hasOwnProperty(constants.recurringMetadata.AMOUNT)) {
                buttonData.recurringMetadata[constants.recurringMetadata.AMOUNT] = buttonData.amount;
                buttonData.recurringMetadata[constants.recurringMetadata.FREQUENCY] = buttonData.frequency;
                buttonData.createCheckoutSessionConfig[constants.RECURRING_METADATA] = buttonData.recurringMetadata;
            } else {
                delete buttonData.createCheckoutSessionConfig[constants.RECURRING_METADATA];
            }
            delete buttonData.paymentDetails[constants.paymentDetails.ALLOW_OVERCHARGE];
            delete buttonData.paymentDetails[constants.paymentDetails.EXTEND_EXPIRATION];
        }

        if (isPaymentMethodOnFileWorkflow(buttonType)) {
            buttonData.createCheckoutSessionConfig[constants.CHARGE_PERMISSION_TYPE] = constants.ChargePermissionTypes.PAYMENT_METHOD_ON_FILE;
        }

        if (isAdditionalPaymentWorkflow(buttonType)) {
            if (schema[constants.PRODUCT_TYPE] === constants.ProductTypes.PAY_ONLY) {
                delete buttonData.createCheckoutSessionConfig[constants.ADDRESS_DETAILS];
            } else {
                buttonData.createCheckoutSessionConfig[constants.ADDRESS_DETAILS] = buttonData.address;
            }
            buttonData.createCheckoutSessionConfig.webCheckoutDetails[constants.webCheckoutDetails.CHECKOUT_MODE] = constants.PROCESS_ORDER;
            delete buttonData.createCheckoutSessionConfig.webCheckoutDetails[constants.webCheckoutDetails.CHECKOUT_REVIEW_RETURN_URL];
            delete buttonData.createCheckoutSessionConfig[constants.DELIVERY_SPECIFICATIONS];
        } else {
            delete buttonData.createCheckoutSessionConfig[constants.ADDRESS_DETAILS];
            delete buttonData.createCheckoutSessionConfig.webCheckoutDetails[constants.webCheckoutDetails.CHECKOUT_MODE];
        }

        buttonData.createCheckoutSessionConfig[constants.PAYMENT_DETAILS] = buttonData.paymentDetails;
        buttonData.createCheckoutSessionConfig[constants.MERCHANT_METADATA] = buttonData.merchantMetadata;
        buttonData.createCheckoutSessionConfig[constants.PROVIDER_METADATA] = buttonData.providerMetadata;

        if(isPaymentMethodOnFileWorkflow(buttonType)) {
            buttonData.createCheckoutSessionConfig[constants.PAYMENT_METHOD_ON_FILE_METADATA] = buttonData.paymentMethodOnFileMetadata;
        }
        
        if (buttonData.chargeAmount.hasOwnProperty(constants.amount.AMOUNT)) {
            buttonData.paymentDetails[constants.paymentDetails.CHARGE_AMOUNT] = buttonData.chargeAmount;
        }

        if (!isRecurringWorkflow(buttonType)) {
            if (buttonData.totalOrderAmount.hasOwnProperty(constants.amount.AMOUNT)) {
                buttonData.paymentDetails[constants.paymentDetails.TOTAL_ORDER_AMOUNT] = buttonData.totalOrderAmount;
            }
        }

        if (buttonData.createCheckoutSessionConfig.hasOwnProperty(constants.PROVIDER_METADATA) && Object.keys(buttonData.createCheckoutSessionConfig[constants.PROVIDER_METADATA]).length != 0) {
            buttonData.createCheckoutSessionConfig[constants.PROVIDER_METADATA] = buttonData.providerMetadata;
        }
        if (buttonData.createCheckoutSessionConfig.hasOwnProperty(constants.MERCHANT_METADATA) && Object.keys(buttonData.createCheckoutSessionConfig[constants.MERCHANT_METADATA]).length != 0) {
            buttonData.createCheckoutSessionConfig[constants.MERCHANT_METADATA] = buttonData.merchantMetadata;
        }

        configString = JSON.stringify(buttonData.createCheckoutSessionConfig);
    }

    let signature = "";
    if (Object.entries(authValues.privateKey).length !== 0) {
        signature = signPayload(configString, authValues);
    }
    const config = {
        "payloadJSON" : configString,
        "signature" : signature
    }

    return config;
}

function buildButtonCode(authValues: any, buttonType:string, config: any, originalButtonSchema: any) {
    const schema = {...originalButtonSchema};

    const height = schema[constants.BUTTON_HEIGHT];
    const width = schema[constants.BUTTON_WIDTH];

    delete schema[constants.BUTTON_HEIGHT];
    delete schema[constants.BUTTON_WIDTH];

    schema["publicKeyId"] = authValues.publicKeyId;

    if (buttonType === constants.ButtonTypes.AMAZON_SIGN_IN) {
        schema[constants.PRODUCT_TYPE] = constants.ProductTypes.SIGN_IN;
        schema[constants.SIGN_IN_CONFIG] = config;
        delete schema[constants.BUTTON_DESIGN];
        delete schema[constants.CREATE_CHECKOUT_SESSION_CONFIG];
    } else {
        schema[constants.CREATE_CHECKOUT_SESSION_CONFIG] = config;
        delete schema[constants.SIGN_IN_CONFIG];
    }

    let divStyle = {};
    let style = ""; // format like: style="height: 500px; width: 1500px"
    if (height) {
        divStyle['height'] = height + "px";
        style = "height: " + height + "px";
        if (width) {
            style += "; ";
        }
    }
    if (width) {
        divStyle['width'] = width + "px";
        style += "width: " + width + "px";
    }
    if (style.length > 0) {
        style = ' style="' + style + '"';
    }

    const copyButtonCode =
`
<div id="AmazonPayButton"${style}></div>
<script src="https://static-${constants.regionMapping[authValues.region]}.payments-amazon.com/checkout.js"></script>
<script type="text/javascript" charset="utf-8">
var amazonPayButton = amazon.Pay.renderButton('#AmazonPayButton', ${JSON.stringify(schema, null, "    ")});
</script>
`;

    return { divStyle: divStyle, copyButtonCode: copyButtonCode, buttonSchema: schema };
}

function buildSdkCodeForDotNet(authValues: any, configString: string) {
    let region = "NotSet";
    switch (authValues.region) {
        case "na":
            region = "UnitedStates";
            break;
        case "eu":
            region = "Europe";
            break;
        case "jp":
            region = "Japan";
            break;
    }
    const payload = configString.replace(/"/g, '\\"');
    const code =

`
using System;
using Amazon.Pay.API;
using Amazon.Pay.API.Types;
using Amazon.Pay.API.WebStore;

class Program
{
    static void Main(string[] args)
    {
        var payConfiguration = new ApiConfiguration
            (
                region: Region.${region},
                environment: Amazon.Pay.API.Types.Environment.Sandbox,
                publicKeyId: "${authValues.publicKeyId}",
                privateKey: "PATH_OR_CONTENT_OF_MY_PRIVATE_KEY"
            );
        var client = new WebStoreClient(payConfiguration);
        var request = "${payload}";
        var signature = client.GenerateButtonSignature(request);
        Console.WriteLine(signature);
    }
}
`;

    return code;
}

function buildSdkCodeForPHP(authValues: any, configString: string) {
    const code =
`
<?php
    include 'vendor/autoload.php';

    $amazonpay_config = array(
        'public_key_id' => '${authValues.publicKeyId}',
        'private_key'   => 'PATH_OR_CONTENT_OF_MY_PRIVATE_KEY',
        'region'        => '${authValues.region}',
        'sandbox'       => true
    );

    $client = new Amazon\\Pay\\API\\Client($amazonpay_config);
    $payload = '${configString}';
    $signature = $client->generateButtonSignature($payload);
    echo $signature . "\\n";
?>
`;
    return code;
}

function buildSdkCodeForJava(authValues: any, configString: string) {
    const region = authValues.region.toUpperCase();
    const payload = configString.replace(/"/g, '\\"');
    const code =

`
import com.amazon.pay.api.AmazonPayClient;
import com.amazon.pay.api.PayConfiguration;
import com.amazon.pay.api.exceptions.AmazonPayClientException;
import com.amazon.pay.api.types.Environment;
import com.amazon.pay.api.types.Region;
import java.nio.file.Files;
import java.nio.file.Paths;
import java.io.IOException;

public class Sample {
    public static void main(String[] args) {
        try {
            final PayConfiguration payConfiguration = new PayConfiguration()
                .setPublicKeyId("${authValues.publicKeyId}")
                .setRegion(Region.${region})
                .setPrivateKey(new String(Files.readAllBytes(Paths.get("PATH_OR_CONTENT_OF_MY_PRIVATE_KEY"))).toCharArray())
                .setEnvironment(Environment.SANDBOX);
            final AmazonPayClient client = new AmazonPayClient(payConfiguration);
            final String payload = "${payload}";
            final String signature = client.generateButtonSignature(payload);
            System.out.println(signature);
        } catch (AmazonPayClientException | IOException e) {
             e.printStackTrace();
        }
    }
}
`;

    return code;
}

function buildSdkCodeForNodeJS(authValues: any, configString: string) {
    const code =

`
const fs = require('fs');
const Client = require('@amazonpay/amazon-pay-api-sdk-nodejs');
const config = {
    publicKeyId: '${authValues.publicKeyId}',
    privateKey: fs.readFileSync('PATH_OR_CONTENT_OF_MY_PRIVATE_KEY'),
    region: '${authValues.region}',
    sandbox: true
};
const client = new Client.AmazonPayClient(config);
const payload = '${configString}';
const signature = client.generateButtonSignature(payload);
console.log(signature);
`;

    return code;
}
